import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import type {SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import type {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {enqueueSnackbar} from "notistack";
import {Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef, useState} from "react";
import type {SurveyAnswerContext} from "../shared/surveyAnswer.util";
import {useSurveyReviewRefresh} from "../shared/surveyQueryRefresh";
import {getSurveyQuestionTypeFacts} from "../shared/surveyQuestionTypeFacts";
import {isWithheldSensitiveAnswer} from "../shared/SurveySensitiveAnswerValue";
import type {SurveyBlackoutPaneHandle} from "./blackout/SurveyBlackoutPane";

/** Which gesture is waiting on the discard confirmation - Cancel in the bar, or closing the dialog. */
export type SurveySubmissionDiscardTarget = 'cancel' | 'close';

export interface SurveySubmissionBlackoutSession {
    /** The answers hold typed text a blackout could cover. Read-only accounts are kept out by the caller. */
    offered: boolean;
    active: boolean;
    dirty: boolean;
    busy: boolean;
    paneRef: RefObject<SurveyBlackoutPaneHandle>;
    /** The raw useState setters: the pane's effects are keyed on them. */
    setBusy: Dispatch<SetStateAction<boolean>>;
    setDirty: Dispatch<SetStateAction<boolean>>;
    enter: () => void;
    cancel: () => void;
    save: () => Promise<void>;
    /** The dialog's own close: it asks first while staged bars would be lost. */
    requestClose: () => void;
    discardTarget: SurveySubmissionDiscardTarget | null;
    confirmDiscard: () => void;
    keepEditing: () => void;
}

interface Args {
    submission: AggregationSurveySubmissionListItem;
    questions: SurveyQuestion[];
    ctx: SurveyAnswerContext;
    open: boolean;
    onClose: () => void;
}

/** Blackout only ever covers typed text: a text answer, or the "other" text of an option question. */
const canHoldTypedText = (question: {type?: SurveyQuestionType; allowOther?: boolean}) =>
    getSurveyQuestionTypeFacts(question.type)?.answer === 'text'
    || !!question.allowOther;

/**
 * The blackout session around the pane: whether it is offered, open, dirty or saving, and the three ways out of
 * it - Cancel and Save in the bar, and closing the dialog. Nothing staged is dropped without asking.
 */
export function useSurveySubmissionBlackoutSession({
    submission,
    questions,
    ctx,
    open,
    onClose
}: Args): SurveySubmissionBlackoutSession {
    const {t} = useLocalize();
    const refreshReviewData = useSurveyReviewRefresh();

    const [active, setActive] = useState(false);
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [discardTarget, setDiscardTarget] = useState<SurveySubmissionDiscardTarget | null>(null);
    const paneRef = useRef<SurveyBlackoutPaneHandle>(null);

    useEffect(() => {
        setActive(false);
        setDirty(false);
        setDiscardTarget(null);
    }, [submission.id, open]);

    /*
        Blackout only ever covers typed text. With nothing typed anywhere - or typed only into a
        withheld sensitive answer of a question that cannot hold text - the mode has nothing to
        offer, and its banner stays out of the way.
    */
    const offered = useMemo(
        () => questions.some(question => {
            const answer = submission.answers?.find(candidate => candidate.questionId === question.id);
            if (!answer) return false;
            if (answer.textValue || answer.customText) return true;
            return isWithheldSensitiveAnswer(answer, question, ctx) && canHoldTypedText(question);
        }),
        [questions, submission, ctx]
    );

    const leave = ({refresh = true} = {}) => {
        setActive(false);
        setDirty(false);
        // The pane goes with the mode, so its in-flight flag has to be dropped here rather than
        // reported down to false by a component that no longer exists.
        setBusy(false);
        if (refresh) void refreshReviewData();
    };

    const cancel = () => {
        // Staged bars are not on the server yet: leaving the mode would silently throw them away.
        if (dirty) {
            setDiscardTarget('cancel');
            return;
        }

        leave();
    };

    const requestClose = () => {
        if (active && dirty) {
            setDiscardTarget('close');
            return;
        }

        onClose();
    };

    const confirmDiscard = () => {
        const target = discardTarget;
        setDiscardTarget(null);
        paneRef.current?.discard();
        leave();
        if (target === 'close') onClose();
    };

    const save = async () => {
        const saved = await paneRef.current?.save();
        if (!saved) return;

        // Save ends the session: the pane already refreshed the masked views as its last step, so
        // the bars just applied come back the way everyone else sees them, with nothing to re-read.
        leave({refresh: false});
        enqueueSnackbar(t('Changes saved!'), {
            variant: 'success',
            anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
        });
    };

    return {
        offered,
        active,
        dirty,
        busy,
        paneRef,
        setBusy,
        setDirty,
        enter: () => setActive(true),
        cancel,
        save,
        requestClose,
        discardTarget,
        confirmDiscard,
        keepEditing: () => setDiscardTarget(null),
    };
}
